import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";

import LoginPage from "@/pages/LoginPage";
import MobileShell from "@/layouts/MobileShell";
import DesktopShell from "@/layouts/DesktopShell";
import MobileDashboard from "@/pages/mobile/Dashboard";
import AktionenHub from "@/pages/mobile/AktionenHub";
import Verlauf from "@/pages/mobile/Verlauf";
import Ausleihen from "@/pages/mobile/Ausleihen";
import MobileArtikel from "@/pages/mobile/Artikel";
import Mehr from "@/pages/mobile/Mehr";
import Einstellungen from "@/pages/mobile/Einstellungen";
import Hilfe from "@/pages/mobile/Hilfe";
import ScanPage from "@/pages/mobile/ScanPage";
import ScanDeepLink from "@/pages/mobile/ScanDeepLink";
import EntnahmeFlow from "@/pages/mobile/flows/Entnahme";
import UmlagerungFlow from "@/pages/mobile/flows/Umlagerung";
import AusleiheFlow from "@/pages/mobile/flows/Ausleihe";
import RueckgabeFlow from "@/pages/mobile/flows/Rueckgabe";
import KorrekturFlow from "@/pages/mobile/flows/Korrektur";
import EingangFlow from "@/pages/mobile/flows/Eingang";
import EntsorgungFlow from "@/pages/mobile/flows/Entsorgung";
import InventoryCountFlow from "@/pages/mobile/flows/InventoryCount";
import PicklisteFlow from "@/pages/mobile/flows/Pickliste";
import UnknownItemFlow from "@/pages/mobile/flows/UnknownItem";
import FotoQueuePage from "@/pages/mobile/FotoQueue";
import OpsQueuePage from "@/pages/mobile/OpsQueue";

import AdminUebersicht from "@/pages/admin/Uebersicht";
import AdminAusleihen from "@/pages/admin/Ausleihen";
import AdminArtikel from "@/pages/admin/Artikel";
import AdminArtikelDetail from "@/pages/admin/ArtikelDetail";
import AdminLagerstruktur from "@/pages/admin/Lagerstruktur";
import AdminBenutzer from "@/pages/admin/Benutzer";
import AdminNachbestellung from "@/pages/admin/Nachbestellung";
import AdminInventur from "@/pages/admin/Inventur";
import AdminPicklisten from "@/pages/admin/Picklisten";
import AdminAudit from "@/pages/admin/Audit";
import AdminDatenqualitaet from "@/pages/admin/Datenqualitaet";
import AdminClarifications from "@/pages/admin/Clarifications";
import AdminEinstellungen from "@/pages/admin/Einstellungen";

import { RequireAdmin, RequireAuth } from "@/components/RoleGuard";
import { RequireDesktop } from "@/components/ViewportGuard";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";

// #137: Toast schließbar + Theme folgt dem aktiven App-Theme statt hart "dark".
function AppToaster() {
    const { theme } = useTheme();
    return (
        <Toaster
            position="top-center"
            richColors
            closeButton
            theme={theme === "light-corporate" ? "light" : "dark"}
            toastOptions={{
                style: {
                    background: "rgb(var(--card))",
                    color: "rgb(var(--foreground))",
                    border: "1px solid rgb(var(--border))",
                },
            }}
        />
    );
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <div className="App">
                    <BrowserRouter>
                        <Routes>
                            <Route path="/login" element={<LoginPage />} />

                            {/* Mobile Floor Mode */}
                            <Route element={<RequireAuth><MobileShell /></RequireAuth>}>
                                <Route index element={<MobileDashboard />} />
                                <Route path="/aktionen" element={<AktionenHub />} />
                                <Route path="/scan" element={<ScanPage />} />
                                <Route path="/scan/handle" element={<ScanDeepLink />} />
                                <Route path="/verlauf" element={<Verlauf />} />
                                <Route path="/ausleihen" element={<Ausleihen />} />
                                <Route path="/artikel" element={<MobileArtikel />} />
                                <Route path="/mehr" element={<Mehr />} />
                                <Route path="/einstellungen" element={<Einstellungen />} />
                                <Route path="/hilfe" element={<Hilfe />} />
                                <Route path="/flows/eingang" element={<EingangFlow />} />
                                <Route path="/flows/entnahme" element={<EntnahmeFlow />} />
                                <Route path="/flows/umlagerung" element={<UmlagerungFlow />} />
                                <Route path="/flows/ausleihe" element={<AusleiheFlow />} />
                                <Route path="/flows/rueckgabe" element={<RueckgabeFlow />} />
                                <Route path="/flows/korrektur" element={<KorrekturFlow />} />
                                <Route path="/flows/entsorgung" element={<EntsorgungFlow />} />
                                <Route path="/flows/inventory-count" element={<InventoryCountFlow />} />
                                <Route path="/flows/pickliste" element={<PicklisteFlow />} />
                                <Route path="/flows/pickliste/:id" element={<PicklisteFlow />} />
                                <Route path="/flows/unknown-item" element={<UnknownItemFlow />} />
                                <Route path="/mehr/fotos-queue" element={<FotoQueuePage />} />
                                <Route path="/mehr/queue" element={<OpsQueuePage />} />
                            </Route>

                            {/* Desktop Admin Console */}
                            <Route
                                element={
                                    <RequireAdmin>
                                        <RequireDesktop>
                                            <DesktopShell />
                                        </RequireDesktop>
                                    </RequireAdmin>
                                }
                            >
                                <Route path="/admin" element={<AdminUebersicht />} />
                                <Route path="/admin/artikel" element={<AdminArtikel />} />
                                <Route path="/admin/ausleihen" element={<AdminAusleihen />} />
                                <Route path="/admin/artikel/:id" element={<AdminArtikelDetail />} />
                                <Route path="/admin/lagerstruktur" element={<AdminLagerstruktur />} />
                                <Route path="/admin/benutzer" element={<AdminBenutzer />} />
                                <Route path="/admin/nachbestellung" element={<AdminNachbestellung />} />
                                <Route path="/admin/inventur" element={<AdminInventur />} />
                                <Route path="/admin/picklisten" element={<AdminPicklisten />} />
                                <Route path="/admin/audit" element={<AdminAudit />} />
                                <Route path="/admin/datenqualitaet" element={<AdminDatenqualitaet />} />
                                <Route path="/admin/clarifications" element={<AdminClarifications />} />
                                <Route path="/admin/einstellungen" element={<AdminEinstellungen />} />
                            </Route>

                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                        <AppToaster />
                    </BrowserRouter>
                </div>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
